import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { t } from "@/i18n";
import { playNeigh } from "@/lib/sounds";
import { hueySpeak, isAutoRead, stopHuey } from "@/lib/voice";
import { SpeakButton, useSpeaking } from "@/components/SpeakButton";

// First-visit one-line tips; which tips were seen is stored in localStorage (no personal data).
const SEEN_KEY = "huey-tips-seen";
const TIPS = [
  [/^\/create\/?$/, "create"],
  [/^\/colour\/?$/, "colour"],
  [/^\/packets\/?$/, "packets"],
  [/^\/colour\/.+/, "studio"],
];
export const tipFor = (pathname) => TIPS.find(([re]) => re.test(pathname))?.[1] || null;
const seen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); } catch { return []; } };
const markSeen = (id) => localStorage.setItem(SEEN_KEY, JSON.stringify([...new Set([...seen(), id])]));

// Returns true for ~900ms after `trigger` changes (used to replay the bounce animation).
export const useBounce = (trigger) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!trigger) return;
    setOn(true);
    const id = setTimeout(() => setOn(false), 950);
    return () => clearTimeout(id);
  }, [trigger]);
  return on;
};

export const useHueyTip = (pathname) => {
  const [tip, setTip] = useState(null);
  useEffect(() => {
    const id = tipFor(pathname);
    if (!id || seen().includes(id)) { setTip(null); return; }
    const show = setTimeout(() => {
      setTip(id);
      playNeigh();
      if (isAutoRead()) setTimeout(() => hueySpeak(`tip-${id}`, t(`huey_tip_${id}`)), 700);
    }, 1200);
    const hide = setTimeout(() => { markSeen(id); setTip(null); }, 1200 + 8000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [pathname]);
  const dismiss = () => { if (tip) { markSeen(tip); stopHuey(); } setTip(null); };
  return [tip, dismiss];
};

export const HueyTip = ({ tip, onDismiss, onOpen, className = "" }) => {
  const bounce = useBounce(tip);
  const talking = useSpeaking() === `tip-${tip}`;
  if (!tip) return null;
  return (
    <div role="status" className={`w-[min(88vw,320px)] bg-white rounded-3xl border-4 border-[#1E293B] shadow-[6px_8px_0_0_#1E293B] p-4 rise ${className}`} data-testid={`huey-tip-${tip}`} dir="auto">
      <div className="flex items-start gap-3">
        <img src="/huey.png" alt="Huey the Horse" className={`w-12 h-12 object-contain shrink-0 ${bounce ? "huey-bounce" : ""} ${talking ? "huey-talk" : ""}`} data-testid="huey-tip-avatar" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-[#FF5252]">{t("huey_tip_label")}</p>
          <p className="text-sm font-medium leading-snug mt-0.5" data-testid="huey-tip-text">{t(`huey_tip_${tip}`)}</p>
        </div>
        <button onClick={onDismiss} className="p-1.5 -m-1.5 rounded-xl hover:bg-slate-100" aria-label="Dismiss tip" data-testid="huey-tip-close-button"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-2 mt-3 pl-[60px]">
        <SpeakButton id={`tip-${tip}`} text={t(`huey_tip_${tip}`)} testid="huey-tip-speak-button" />
        <button onClick={onDismiss} className="text-xs font-bold bg-[#FFD166] border-2 border-[#1E293B] rounded-full px-3 py-1.5" data-testid="huey-tip-dismiss-button">{t("huey_tip_dismiss")}</button>
        {onOpen && <button onClick={() => { onDismiss(); onOpen(); }} className="text-xs font-bold border-2 border-slate-200 hover:border-[#1E293B] rounded-full px-3 py-1.5" data-testid="huey-tip-ask-button">{t("huey_ask")}</button>}
      </div>
    </div>
  );
};
